import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MergeFieldInserter } from '@/components/messaging/merge-field-inserter';
import { Tip } from '@/components/shared/tip';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Listbox } from '@/components/ui/listbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { TimePicker } from '@/components/ui/time-picker';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { insertAtSelection } from '@/lib/messaging-merge-fields';
import { usePreferences } from '@/stores/preferences-store';

export type AutomationChannel = 'email' | 'sms' | 'push';

export interface AutomationConfiguration {
  sendTime: string;
  subject: string;
  body: string;
  weekday?: number;
  absenceDays?: number;
  hoursBefore?: number;
}

export interface AutomationRow {
  id: string;
  type: string;
  name: string;
  isEnabled: boolean;
  runnable: boolean;
  configuration: AutomationConfiguration;
  channels: AutomationChannel[];
  lastRunAt: string | null;
  sendCount: number;
}

interface AutomationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  automation: AutomationRow | null;
}

export const AutomationDialog = ({ open, onOpenChange, automation }: AutomationDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();
  const churchTimezone = usePreferences((state) => state.timezone);

  const [sendTime, setSendTime] = useState<string | null>('09:00');
  const [body, setBody] = useState('');
  const [weekday, setWeekday] = useState<number>(6);
  const [absenceDays, setAbsenceDays] = useState('21');
  const [hoursBefore, setHoursBefore] = useState('24');
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  useEffect(() => {
    if (!open || !automation) return;
    const config = automation.configuration;
    setSendTime(config.sendTime || '09:00');
    setBody(config.body || '');
    setWeekday(config.weekday ?? 6);
    setAbsenceDays(String(config.absenceDays ?? 21));
    setHoursBefore(String(config.hoursBefore ?? 24));
  }, [open, automation]);

  const rememberSelection = () => {
    const el = bodyRef.current;
    if (!el) return;
    setSelection({
      start: el.selectionStart ?? body.length,
      end: el.selectionEnd ?? body.length,
    });
  };

  const insertInto = (token: string) => {
    const { value: next, caret } = insertAtSelection(body, selection.start, selection.end, token);
    setBody(next);
    setSelection({ start: caret, end: caret });
    requestAnimationFrame(() => {
      bodyRef.current?.focus();
      bodyRef.current?.setSelectionRange(caret, caret);
    });
  };

  const absenceDaysNum = Number(absenceDays);
  const hoursBeforeNum = Number(hoursBefore);
  const absenceValid =
    Number.isInteger(absenceDaysNum) && absenceDaysNum >= 7 && absenceDaysNum <= 90;
  const hoursValid =
    Number.isInteger(hoursBeforeNum) && hoursBeforeNum >= 1 && hoursBeforeNum <= 72;

  const canSubmit =
    Boolean(automation) &&
    body.trim() &&
    Boolean(sendTime) &&
    (automation?.type !== 'absence_follow_up' || absenceValid) &&
    ((automation?.type !== 'service_reminder' && automation?.type !== 'event_reminder') ||
      hoursValid);

  const save = useMutation({
    mutationFn: () => {
      if (!automation || !sendTime) throw new Error('missing');
      const trimmedBody = body.trim();
      const configuration: AutomationConfiguration = {
        sendTime,
        subject: (automation.configuration.subject || trimmedBody).slice(0, 200),
        body: trimmedBody,
      };
      if (automation.type === 'tithe_reminder') configuration.weekday = weekday;
      if (automation.type === 'absence_follow_up') configuration.absenceDays = absenceDaysNum;
      if (automation.type === 'service_reminder' || automation.type === 'event_reminder') {
        configuration.hoursBefore = hoursBeforeNum;
      }

      return api.patch<AutomationRow>(`/messaging/automations/${automation.id}`, {
        channels: ['sms'],
        configuration,
      });
    },
    onSuccess: () => {
      toast.success(t('automations.saved'));
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'automations'] });
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  if (!automation) return null;

  const showSendTime =
    automation.type !== 'service_reminder' &&
    automation.type !== 'event_reminder' &&
    automation.type !== 'first_timer_welcome' &&
    automation.type !== 'contribution_receipt' &&
    automation.type !== 'pledge_confirmation' &&
    automation.type !== 'devotional_publish';

  const showHoursBefore =
    automation.type === 'service_reminder' || automation.type === 'event_reminder';

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>{t('automations.configureTitle', { name: automation.name })}</DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[75vh] pr-2">
          <div className="space-y-4">
            <p className="text-body-sm text-fg-secondary">
              {t(`automations.types.${automation.type}.description`, {
                defaultValue: t('automations.description'),
              })}
            </p>

            {showSendTime ? (
              <div>
                <Label>{t('automations.sendTime')}</Label>
                <TimePicker
                  value={sendTime}
                  onChange={setSendTime}
                  minuteStep={5}
                  popoverPortal="dialog"
                />
                <p className="mt-1 text-caption text-fg-secondary">
                  {t('automations.sendTimeTimezone', { timezone: churchTimezone })}
                </p>
                <Tip tipId="messaging.automationSendTime" />
              </div>
            ) : null}

            {automation.type === 'tithe_reminder' ? (
              <div>
                <Label>{t('automations.weekday')}</Label>
                <Listbox
                  value={String(weekday)}
                  onChange={(value) => setWeekday(Number(value ?? 6))}
                  options={[1, 2, 3, 4, 5, 6, 7].map((day) => ({
                    value: String(day),
                    label: t(`automations.weekdays.${day}`),
                  }))}
                  popoverPortal="dialog"
                />
              </div>
            ) : null}

            {automation.type === 'absence_follow_up' ? (
              <div>
                <Label htmlFor="automation-absence-days">{t('automations.absenceDays')}</Label>
                <Input
                  id="automation-absence-days"
                  value={absenceDays}
                  onChange={(e) => setAbsenceDays(e.target.value)}
                  invalid={!absenceValid}
                />
                <p className="mt-1 text-caption text-fg-secondary">
                  {t('automations.absenceDaysHelp')}
                </p>
              </div>
            ) : null}

            {showHoursBefore ? (
              <div>
                <Label htmlFor="automation-hours-before">
                  {automation.type === 'event_reminder'
                    ? t('automations.hoursBeforeEvent')
                    : t('automations.hoursBefore')}
                </Label>
                <Input
                  id="automation-hours-before"
                  value={hoursBefore}
                  onChange={(e) => setHoursBefore(e.target.value)}
                  invalid={!hoursValid}
                />
                <p className="mt-1 text-caption text-fg-secondary">
                  {automation.type === 'event_reminder'
                    ? t('automations.hoursBeforeEventHelp')
                    : t('automations.hoursBeforeHelp')}
                </p>
              </div>
            ) : null}

            <div>
              <Label htmlFor="automation-body">{t('automations.body')}</Label>
              <Textarea
                ref={bodyRef}
                id="automation-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onSelect={rememberSelection}
                onKeyUp={rememberSelection}
                onClick={rememberSelection}
                onBlur={rememberSelection}
                rows={5}
              />
              <MergeFieldInserter
                onInsert={insertInto}
                includeGivingFields={
                  automation.type === 'contribution_receipt' || automation.type === 'pledge_confirmation'
                }
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {tCommon('action.cancel')}
              </Button>
              <Button
                disabled={!canSubmit || save.isPending}
                onClick={() => save.mutate()}
              >
                {tCommon('action.saveChanges')}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
